// @ts-strict-ignore
import Space from 'antd/lib/space';
import Tabs from 'antd/lib/tabs';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import * as React from 'react';
import { AppComponentProps } from '../../../typescriptHelpers';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { SearchOutlined, KeyOutlined, PlusOutlined, MobileOutlined, ProfileOutlined } from '@ant-design/icons';
import Table from 'antd/lib/table';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import { getIsApplePlatform } from '../../../helpers';
import { SecurityScanOutlined, DeleteOutlined } from '@ant-design/icons';
import Form from 'antd/lib/form';
import * as AnalyticEvents from '../../../utils/analytics';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import { UploadSigningKeyFormFields } from '../UploadSigningKeyFormFields';
import { AppPlatform, AppSettings, SigningKey } from '../../../types';
import Input from 'antd/lib/input/Input';
import { useUploadFormWithFile } from '../../../utils/hooks/useUploadFormWithFile';
import { appEndpoint } from '../../../helpers/apiHelpers';
import { globalMessage } from '../../../utils/globalMessage';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import {
    selectSigningKeys,
    selectAppUsersById,
    selectAllTrackingPropertiesForRelease,
    selectIsAppsDemoModeEnabled,
} from '../../../selectors/selectors';
import { OwnerAvatar } from '../../Avatars';
import { UsernameAvatar } from '../../buildDistribution/CommonComponents';
import {
    DeleteSigningKeyRequest,
    tryUpdateAppSettings,
    updateAppSettingsSuccessAction,
} from '../../../actions/appSettings';
import PermissionedActionButton from '../../HOCs/PermissionedActionButton';
import { AppSettingsUpdatable } from '../../../actions/updatable';
import { RunwayUserAction } from '../../../helpers/aclHelpers';
import Tooltip from 'antd/lib/tooltip';
import { displayUserNameLong } from '../../../helpers/displayHelpers';
import { DevicesSection } from './DevicesSection';
import { ProfilesSection } from './ProfilesSection';
import { useAppPlatform, useError, useGetEntity, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { FetchablePaginatedEntityType } from '@/entityType';

enum SectionTab {
    SigningKeys = 'Signing keys',
    Keystores = 'Keystores',
    Devices = 'Devices',
    Profiles = 'Profiles',
}

const IOS_SECTIONS = [SectionTab.Devices, SectionTab.Profiles, SectionTab.SigningKeys];
const ANDROID_SECTIONS = [SectionTab.Keystores];

const renderTitle = (title: string) => {
    return <Text strong>{title}</Text>;
};

const getExtension = (filename: string) => {
    if (!filename) {
        return '';
    }
    const split = filename.split('.');
    return `.${split[split.length - 1]}`;
};

const SecurityText = () => {
    return (
        <div
            style={{
                display: 'inline-block',
            }}
        >
            <Space>
                <SecurityScanOutlined
                    style={{
                        color: antGrey.grey7,
                    }}
                />
                <Text type="secondary">We take security seriously.</Text>
                <Tooltip
                    overlayStyle={{
                        maxWidth: 600,
                    }}
                    title={
                        <Space direction="vertical">
                            <Text
                                style={{
                                    color: runwayColors.white,
                                }}
                            >
                                We take security seriously and have a robust system in place for keeping your signing
                                keys safe. Signing keys are encrypted both in transit and while at rest and, once
                                uploaded, they are inaccessible from the open internet – our signing server sits in a
                                virtual private cloud (VPC).
                            </Text>

                            <Text
                                style={{
                                    color: runwayColors.white,
                                }}
                            >
                                Signing keys are exclusively used for re-signing binaries – during this process, the
                                signing key of the original binary is checked and must match the signing key being used
                                for re-signing. This means that signing keys can never be used to sign a binary that
                                wasn&apos;t previously signed with the same signing key.
                            </Text>
                            <Text
                                style={{
                                    color: runwayColors.white,
                                }}
                            >
                                For more details, visit our{' '}
                                <Link href="https://www.runway.team/security" target="_blank">
                                    documentation
                                </Link>
                                .
                            </Text>
                        </Space>
                    }
                >
                    <Text
                        style={{
                            color: runwayColors.info,
                        }}
                    >
                        Learn more
                    </Text>
                </Tooltip>
            </Space>
        </div>
    );
};

const UserAvatarAndName: React.FC<{ userId: string; appId: string }> = ({ userId, appId }) => {
    const users = useSelector((state: RootState) => selectAppUsersById(state, appId, userId ? [userId] : []) ?? []);

    const hasFoundUser = users.length > 0;
    const userName = hasFoundUser ? displayUserNameLong(users[0]) : userId;

    return (
        <Space>
            {users.length === 0 ?
                <UsernameAvatar name={userId} />
            :   <OwnerAvatar user={users[0]} />}
            <Text>{userName}</Text>
        </Space>
    );
};

export interface SigningKeysOrKeystoresProps extends AppComponentProps {
    setIsAddSigningKeyModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
    keyNomenclature: string;
    getAllDevicesIsLoading: boolean;
}

const ConfirmRemoveSigningKeyButton: React.FC<{
    keyId: string;
    appId: string;
    orgId: string;
    keyNomenclature: string;
}> = ({ keyId, appId, orgId, keyNomenclature }) => {
    const isLoading = useIsLoading(AppSettingsUpdatable.DeleteSigningKey, keyId);
    const success = useUpdated(AppSettingsUpdatable.DeleteSigningKey, keyId);
    const error = useError(AppSettingsUpdatable.DeleteSigningKey, keyId);

    const dispatch = useDispatch();

    const removeSigningKey = (keyId: string) => {
        const deleteMonitoringMetricRequest: DeleteSigningKeyRequest = {
            orgId,
            appId,
            keyId,
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.DeleteSigningKey, deleteMonitoringMetricRequest, keyId));
    };

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    React.useEffect(() => {
        if (success) {
            globalMessage.success(`Successfully removed ${keyNomenclature.toLowerCase()}`);
        }
    }, [success]);

    return (
        <Popconfirm
            title={`Are you sure you want to delete this ${keyNomenclature.toLowerCase()}?`}
            okText={`Yes, remove ${keyNomenclature.toLowerCase()}`}
            placement="topRight"
            okButtonProps={{ danger: true }}
            onConfirm={() => {
                removeSigningKey(keyId);
            }}
        >
            <PermissionedActionButton
                danger
                type="text"
                actionType={RunwayUserAction.UAUpdateAppSettings}
                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                loading={isLoading}
            >
                Delete
            </PermissionedActionButton>
        </Popconfirm>
    );
};

export const SigningKeysSection: React.FC<SigningKeysOrKeystoresProps> = ({
    appId,
    orgId,
    setIsAddSigningKeyModalVisible,
    keyNomenclature,
}) => {
    const signingKeys = useSelector((state: RootState) => selectSigningKeys(state, appId) ?? []);

    return (
        <div
            style={{
                width: '100%',
                marginTop: 32,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                alignItems: 'stretch',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    flex: 1,
                }}
            >
                <Text
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: antGrey.grey8,
                    }}
                >
                    Signing keys
                </Text>
                <PermissionedActionButton
                    type="primary"
                    actionType={RunwayUserAction.UAUploadSigningKey}
                    icon={<PlusOutlined />}
                    onClick={() => setIsAddSigningKeyModalVisible(true)}
                >
                    Upload a new signing key
                </PermissionedActionButton>
            </div>
            <Table
                dataSource={signingKeys}
                locale={{
                    emptyText: (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 12,
                                backgroundColor: antGrey.grey2,
                                padding: 32,
                                borderRadius: 8,
                            }}
                        >
                            <KeyOutlined style={{ color: antGrey.grey5, fontSize: 32 }} />
                            <Text
                                style={{
                                    color: antGrey.grey7,
                                }}
                                strong
                            >
                                No signing keys found
                            </Text>
                            <Text
                                style={{
                                    color: antGrey.grey7,
                                }}
                            >
                                Signing keys are used for re-signing binaries.
                            </Text>
                        </div>
                    ),
                }}
                columns={[
                    {
                        title: renderTitle('Name'),
                        key: 'name',
                        render: (signingKey: SigningKey) => signingKey.name,
                    },
                    {
                        title: renderTitle('Uploaded by'),
                        key: 'uploadedBy',
                        render: (signingKey: SigningKey) => (
                            <UserAvatarAndName userId={signingKey.uploadedBy} appId={appId} />
                        ),
                    },
                    {
                        title: renderTitle('Extension'),
                        key: 'extension',
                        render: (signingKey: SigningKey) => <Text code>{getExtension(signingKey.filename)}</Text>,
                    },
                    {
                        title: null,
                        key: 'actions',
                        width: 100,
                        render: (signingKey: SigningKey) => (
                            <ConfirmRemoveSigningKeyButton
                                keyNomenclature={keyNomenclature}
                                keyId={signingKey.id}
                                appId={appId}
                                orgId={orgId}
                            />
                        ),
                    },
                ]}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
            />
            <div
                style={{
                    marginTop: 24,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <SecurityText />
            </div>
        </div>
    );
};

export const KeystoresSection: React.FC<SigningKeysOrKeystoresProps> = ({
    appId,
    orgId,
    setIsAddSigningKeyModalVisible,
    keyNomenclature,
}) => {
    const signingKeys = useSelector((state: RootState) => selectSigningKeys(state, appId) ?? []);

    return (
        <div
            style={{
                width: '100%',
                marginTop: 32,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                alignItems: 'stretch',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    flex: 1,
                }}
            >
                <Text
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: antGrey.grey8,
                    }}
                >
                    Signing keys
                </Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddSigningKeyModalVisible(true)}>
                    Upload keystore
                </Button>
            </div>
            <Table
                dataSource={signingKeys}
                locale={{
                    emptyText: (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 12,
                                backgroundColor: antGrey.grey2,
                                padding: 32,
                                borderRadius: 8,
                            }}
                        >
                            <KeyOutlined style={{ color: antGrey.grey5, fontSize: 32 }} />
                            <Text
                                style={{
                                    color: antGrey.grey7,
                                }}
                                strong
                            >
                                No signing keys found
                            </Text>
                            <Text
                                style={{
                                    color: antGrey.grey7,
                                }}
                            >
                                Signing keys are used for re-signing binaries.
                            </Text>
                        </div>
                    ),
                }}
                columns={[
                    {
                        title: renderTitle('Name'),
                        key: 'name',
                        render: (signingKey: SigningKey) => signingKey.name,
                    },
                    {
                        title: renderTitle('Uploaded by'),
                        key: 'uploadedBy',
                        render: (signingKey: SigningKey) => (
                            <UserAvatarAndName userId={signingKey.uploadedBy} appId={appId} />
                        ),
                    },
                    {
                        title: renderTitle('Extension'),
                        key: 'extension',
                        render: (signingKey: SigningKey) => <Text code>{getExtension(signingKey.filename)}</Text>,
                    },
                    {
                        title: null,
                        key: 'actions',
                        width: 100,
                        render: (signingKey: SigningKey) => (
                            <ConfirmRemoveSigningKeyButton
                                keyNomenclature={keyNomenclature}
                                keyId={signingKey.id}
                                appId={appId}
                                orgId={orgId}
                            />
                        ),
                    },
                ]}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
            />
            <div
                style={{
                    marginTop: 24,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <SecurityText />
            </div>
        </div>
    );
};

const componentForSection = (section: SectionTab, props: SigningKeysOrKeystoresProps) => {
    switch (section) {
        case SectionTab.SigningKeys:
            return <SigningKeysSection {...props} />;
        case SectionTab.Keystores:
            return <KeystoresSection {...props} />;
        case SectionTab.Devices:
            return <DevicesSection {...props} />;
        case SectionTab.Profiles:
            return <ProfilesSection {...props} />;
        default:
            return null;
    }
};

const iconForSection = (section: SectionTab, isActiveTab: boolean) => {
    const style: React.CSSProperties = { color: isActiveTab ? runwayColors.primary : antGrey.grey7 };

    switch (section) {
        case SectionTab.SigningKeys:
            return <SearchOutlined style={style} />;
        case SectionTab.Keystores:
            return <SearchOutlined style={style} />;
        case SectionTab.Devices:
            return <MobileOutlined style={style} />;
        case SectionTab.Profiles:
            return <ProfileOutlined style={style} />;
        default:
            return null;
    }
};

interface AddSigningKeyModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
    platform: AppPlatform;
    keyNomenclature: string;
}

const AddSigningKeyModal: React.FC<AddSigningKeyModalProps> = (props) => {
    const { keyNomenclature } = props;
    const [form] = Form.useForm();

    const [uploadResult, uploadIsLoading, uploadError, upload] = useUploadFormWithFile<
        Record<string, string>,
        AppSettings
    >();

    const appUrl = appEndpoint(props.orgId, props.appId);

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (uploadError) {
            globalMessage.error(uploadError.message);
        }
    }, [uploadError]);

    React.useEffect(() => {
        if (uploadResult) {
            globalMessage.success(`Successfully uploaded ${keyNomenclature.toLowerCase()}`);
            dispatch(
                updateAppSettingsSuccessAction({
                    appId: props.appId,
                    appSettings: uploadResult,
                }),
            );
            props.onCancel();
        }
    }, [uploadResult]);

    return (
        <Modal
            title={`Upload a new ${keyNomenclature.toLowerCase()}`}
            onCancel={props.onCancel}
            width={600}
            confirmLoading={uploadIsLoading}
            open={props.open}
            okButtonProps={{ disabled: uploadIsLoading }}
            onOk={() => form.submit()}
            okText="Upload"
            afterClose={() => {
                form.resetFields();
            }}
            destroyOnClose
        >
            <UploadSigningKeyFormFields
                appId={props.appId}
                orgId={props.orgId}
                onUpload={(values) => {
                    const { file, ...rest } = values;
                    const formValues = rest as Record<string, string>;
                    upload({
                        file,
                        uploadUrl: `${window.location.origin}/${appUrl}/appStore/signingKey`,
                        formValues,
                    });
                }}
                form={form}
                prefixContent={
                    <>
                        <Text
                            type="secondary"
                            style={{
                                marginBottom: 24,
                            }}
                        >
                            Upload a signing key to sign and re-sign binaries.
                        </Text>
                        <Form.Item
                            label={`${keyNomenclature} name`}
                            name="name"
                            rules={[
                                {
                                    required: true,
                                    message: `Please enter a ${keyNomenclature.toLowerCase()} name`,
                                },
                            ]}
                        >
                            <Input placeholder={`${keyNomenclature} name`} />
                        </Form.Item>
                        <Divider
                            style={{
                                margin: 0,
                                marginBottom: 24,
                            }}
                        />
                    </>
                }
                extraContent={<SecurityText />}
            />
        </Modal>
    );
};

export const ProfilesAndDevicesOrSigningKeys: React.FC<AppComponentProps> = (props) => {
    const { appId, orgId } = props;

    const appPlatform = useAppPlatform({ orgId, appId });
    const isApple = getIsApplePlatform(appPlatform);
    const sections = isApple ? IOS_SECTIONS : ANDROID_SECTIONS;
    const [activeTab, setActiveTab] = React.useState(sections[0]);

    const [isAddSigningKeyModalVisible, setIsAddSigningKeyModalVisible] = React.useState(false);
    const keyNomenclature = isApple ? 'Signing key' : 'Keystore';

    const allProperties = useSelector((state: RootState) =>
        selectAllTrackingPropertiesForRelease(state, props.orgId, props.appId),
    );

    const [getAllDevices, getAllDevicesIsLoading, getAllDevicesError] = useGetEntity(
        FetchablePaginatedEntityType.AllDevices,
        appId,
    );

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    React.useEffect(() => {
        if (!isViewingAppsInDemoMode) {
            getAllDevices({
                appId,
                orgId,
            });
        }
    }, [isViewingAppsInDemoMode]);

    React.useEffect(() => {
        if (getAllDevicesError) {
            globalMessage.error(getAllDevicesError);
        }
    }, [getAllDevicesError]);

    React.useEffect(() => {
        AnalyticEvents.track(AnalyticEvents.Event.ViewProfilesAnDevices, {
            ...allProperties,
            section: activeTab,
        });
    }, [sections]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Tabs
                type="card"
                destroyInactiveTabPane
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key as SectionTab);
                }}
                items={sections.map((section) => {
                    const isActiveTab = activeTab === section;
                    return {
                        label: (
                            <Space>
                                {iconForSection(section, isActiveTab)}
                                <Text
                                    style={{
                                        color: isActiveTab ? runwayColors.primary : antGrey.grey7,
                                    }}
                                >
                                    {section}
                                </Text>
                            </Space>
                        ),
                        key: section,
                        children: componentForSection(section, {
                            keyNomenclature,
                            setIsAddSigningKeyModalVisible,
                            getAllDevicesIsLoading,
                            ...props,
                        }),
                    };
                })}
            />

            <AddSigningKeyModal
                appId={appId}
                orgId={orgId}
                open={isAddSigningKeyModalVisible}
                onCancel={() => setIsAddSigningKeyModalVisible(false)}
                platform={appPlatform}
                keyNomenclature={keyNomenclature}
            />
        </div>
    );
};
